<template>
  <div class="d-flex align-items-center gap-2">
    <div class="flex-grow-1">
      <input
        ref="fileInput"
        type="file"
        v-show="false"
        class="form-control"
        :class="{ 'border-primary': file }"
        style="background: none"
        :id="id"
        :accept="effectiveAccept"
        @change="onFileChange"
        :required="required"
      />
      <small v-if="file">
        {{ file.name }}
      </small>
      <small v-if="isUploading && !file" class="text-primary">
        Subiendo archivo...
      </small>
      <div v-if="isUploading" class="mt-2">
        <small class="text-primary">
          <span
            class="spinner-border spinner-border-sm me-1"
            role="status"
          ></span>
          Subiendo archivo...
        </small>
      </div>
      <div class="mt-2 d-flex align-items-center gap-2">
        <small
          ><i class="material-icons text-primary" v-if="url">attach_file</i>
          <a
            v-if="url"
            href="#"
            @click.prevent="openFile"
            class="text-primary text-decoration-none"
          >
            {{ getFileName(url) }}
          </a></small
        >
        <button
          v-if="url || file"
          type="button"
          class="btn btn-sm btn-outline-danger"
          @click="deleteFile"
          title="delete file"
        >
          <i class="material-icons">delete</i>
        </button>
        <button
          v-if="url"
          type="button"
          class="btn btn-sm btn-outline-secondary"
          title="change file"
          @click="triggerFileInput"
        >
          <i class="material-icons">edit</i>
        </button>
        <button
          v-if="!url && !file"
          type="button"
          class="btn btn-sm btn-outline-primary"
          title="upload file"
          @click="triggerFileInput"
        >
          <i class="material-icons">upload</i> Upload file
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import {
  isDocumentUploadAllowed,
  resolveDocumentFieldAccept,
} from "@/utils/documentUpload";

export default {
  name: "UploadFile",
  props: {
    id: {
      type: String,
      required: true,
    },
    accept: {
      type: String,
      default: "*",
    },
    required: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [String, Object],
      default: null,
    },
  },
  data() {
    return {
      file: null,
      url: null,
      isUploading: false,
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (typeof newVal === "string" && newVal) {
          this.url = newVal;
          this.file = null;
        }
      },
    },
  },
  computed: {
    effectiveAccept() {
      return resolveDocumentFieldAccept(this.accept);
    },
  },
  methods: {
    getFileName(url) {
      try {
        // Si es una fileKey (no empieza con http), extraer el nombre del archivo
        if (!url.startsWith("http")) {
          // La fileKey tiene formato: timestamp_nombrearchivo.ext
          const parts = url.split("_");
          if (parts.length > 1) {
            return parts.slice(1).join("_"); // Retorna todo después del timestamp
          }
          return url;
        }

        // Si es una URL completa, extraer el nombre del archivo
        const urlObj = new URL(url);
        const pathname = urlObj.pathname;
        const fileName = pathname.split("/").pop();
        return decodeURIComponent(fileName);
      } catch (e) {
        return url;
      }
    },
    onFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (!isDocumentUploadAllowed(file)) {
        alert(this.$t("Document upload type not allowed"));
        event.target.value = "";
        return;
      }

      this.file = file;

      // Automatically trigger upload when file is selected
      this.uploadFile();
    },

    deleteFile() {
      this.url = null;
      this.file = null;
      this.$emit("input", null);
    },

    async uploadFile() {
      if (!this.file) return;

      try {
        this.isUploading = true;

        const formData = new FormData();
        formData.append("image", this.file);

        const response = await wencesApi.post("/api/upload/upload", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        const fileKey = response.data.fileKey;
        this.url = fileKey;
        this.file = null;

        this.$emit("input", fileKey);
      } catch (error) {
        console.error("Error al subir el archivo:", error);
        alert("Error al subir el archivo. Por favor, intente nuevamente.");
      } finally {
        this.isUploading = false;
      }
    },

    async getPresignedUrl(fileKey) {
      try {
        // Extraer solo el nombre del archivo sin el prefijo fileuploads/
        let cleanFileKey = fileKey;
        if (fileKey.startsWith("fileuploads/")) {
          cleanFileKey = fileKey.replace("fileuploads/", "");
        }

        const response = await wencesApi.get(
          `/api/upload/file/${cleanFileKey}`
        );
        return response.data.url;
      } catch (error) {
        console.error("Error al obtener URL firmada:", error);
        return null;
      }
    },

    async openFile() {
      if (!this.url) return;

      if (!this.url.startsWith("http")) {
        const presignedUrl = await this.getPresignedUrl(this.url);
        if (presignedUrl) {
          window.open(presignedUrl, "_blank");
        } else {
          alert("Error al acceder al archivo.");
        }
      } else {
        window.open(this.url, "_blank");
      }
    },

    triggerFileInput() {
      // This method is called when the "upload file" button is clicked
      // It should trigger the file input to open the file dialog
      const fileInput = this.$refs.fileInput;
      if (fileInput) {
        fileInput.click();
      }
    },
  },
};
</script>
